import AbstractService from 'services/AbstractService';
import ProcessService from 'services/ProcessService';
import TabsService from 'services/TabsService';

/**
 * Implements Security Service API methods
 */
class SecurityService extends AbstractService {
    constructor() {
        super();
        this.USERS_FETCH_START = 'USERS_FETCH_START';
        this.USERS_FETCH_SUCCESS = 'USERS_FETCH_SUCCESS';
        this.USERS_FETCH_ERROR = 'USERS_FETCH_ERROR';
        this.PERMISSIONS_FETCH_START = 'PERMISSIONS_FETCH_START';
        this.PERMISSIONS_FETCH_SUCCESS = 'PERMISSIONS_FETCH_SUCCESS';
        this.PERMISSIONS_FETCH_ERROR = 'PERMISSIONS_FETCH_ERROR';
        this.LOGIN_START = 'LOGIN_START';
        this.LOGIN_SUCCESS = 'LOGIN_SUCCESS';
        this.LOGIN_ERROR = 'LOGIN_ERROR';
        this.POLL_START = 'POLL_START';
        this.POLL_SUCCESS = 'POLL_SUCCESS';
        this.POLL_ERROR = 'POLL_ERROR';
        this.LOGOUT_START = 'LOGOUT_START';
        this.LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
        this.LOGOUT_ERROR = 'LOGOUT_ERROR';
        this.LOGIN_SSO_START = 'LOGIN_SSO_START';
        this.LOGIN_SSO_ERROR = 'LOGIN_SSO_ERROR';
        this.LOGIN_SSO_SUCCESS = 'LOGIN_SSO_SUCCESS';
        this.BUILD_VERSION_UPDATED = 'BUILD_VERSION_UPDATED';
        this.UPDATE_WRITABLE_PROCESSES = 'UPDATE_WRITABLE_PROCESSES';

        // We are injecting this from node.js environment variables set during the build
        this.baseUrl = process.env.REACT_APP_BASE_URL;
    }

    getReducer() {
        return (state = this.getInitialState(), action) => {
            let newState = state;

            switch (action.type) {
                case this.LOGIN_SUCCESS:
                    newState = Object.assign({}, state);
                    //TODO - change login service to return a json object instead of array
                    const json = action.response;
                    localStorage.setItem('sessionId', json[3]);
                    const name = json[0];
                    newState.username = name;
                    newState.userLoggedIn = true;
                    break;

                case this.LOGOUT_SUCCESS:
                    newState = Object.assign({}, state);
                    newState = Object.assign(newState, this._getLogoutState());
                    break;

                case this.LOGIN_SSO_SUCCESS:
                    newState = Object.assign({}, state);
                    newState.username = action.response.username;
                    newState.userLoggedIn = true;
                    break;

                case this.POLL_START:
                    newState = Object.assign({}, state);
                    newState.pollingInterval = action.pollingInterval;
                    break;

                case this.POLL_SUCCESS:
                    newState = Object.assign({}, state);
                    newState.sessionMaxLifetime = parseInt(action.response.maxlifetime, 10) * 1000;
                    break;

                case this.USERS_FETCH_SUCCESS:
                    newState = Object.assign({}, state);
                    const users = action.response.reduce((usersObj, user) => {
                        let newUserObj = Object.assign({}, usersObj);

                        if (user.lastName && user.email) {
                            //filter out garbage users that don't actually exist
                            newUserObj = Object.assign({}, usersObj,
                                { [user.username]: user }
                            );
                        }
                        return newUserObj;
                    }, {});
                    newState.users = users;
                    break;

                case this.PERMISSIONS_FETCH_SUCCESS:
                    newState = Object.assign({}, state);
                    newState.writableProcesses = action.response;
                    break;

                case this.UPDATE_WRITABLE_PROCESSES:
                case ProcessService.PROCESS_SAVE_SUCCESS:
                case ProcessService.PROCESS_NEW:
                    newState = Object.assign({}, state);
                    const newWritableProcesses = Object.assign({}, state.writableProcesses,
                        { [action.processId]: [] }
                    );
                    newState.writableProcesses = newWritableProcesses;
                    break;

                case TabsService.TAB_CLOSE:
                    newState = Object.assign({}, state);

                    const processState = ProcessService.getMyState(this.getStore().getState());
                    const tabIds = action.tabIds;

                    tabIds.forEach((tab) => {
                        const tabObj = TabsService.getObjFromTabText(tab);
                        const processId = tabObj.processId;
                        const revisionNum = tabObj.revisionNum;

                        if (!revisionNum) {
                            //if it's a new unsaved process remove from writable processes list
                            if (!(processId in processState.processesById) && newState.writableProcesses) {
                                delete newState.writableProcesses[processId];
                            }
                        }
                    });

                    break;

                case ProcessService.PROCESS_OWNERS_SAVE_SUCCESS:
                    newState = Object.assign({}, state);

                    if (action.response.indexOf(state.username) < 0) {
                        //is logged in user is no longer in the list of owners, remove the process from writable list
                        delete newState.writableProcesses[action.processId];
                    }
                    break;

                default:
                    break;
            }
            return newState;
        };
    }

    _getLogoutState() {
        localStorage.removeItem('sessionId');
        return {
            username: null,
            userLoggedIn: false,
            writableProcesses: null
        };
    }

    _getPollErrorState(state) {
        const pollErrorCount = state.pollErrorCount + 1;
        let loggedOutState = {};
        if ((pollErrorCount * state.pollingInterval) > state.sessionMaxLifetime) {
            loggedOutState = this._getLogoutState();
        }

        return Object.assign({
            pollErrorCount
        }, loggedOutState);
    }

    getInitialState() {
        return {
            username: null,
            writableProcesses: null,
            userLoggedIn: false,
            sessionMaxLifetime: 0,
            pollErrorCount: 0,
            pollingInterval: 0,
            users: null
        };
    }

    getStoreKey() {
        return 'security';
    }

    dispatchPollRequest = (pollingInterval) => {
        //fire the first request immediately so auto-login completes before user has the chance to edit something:
        // this.dispatch(
        //     {
        //         type: this.POLL_START,
        //         pollingInterval
        //     }
        // );

        const instance = this;
        setTimeout(() => {
            instance.dispatch(
                {
                    type: instance.POLL_START,
                    pollingInterval
                }
            );
            instance.dispatchPollRequest(pollingInterval);
        }, pollingInterval);
    };

    login(username, password) {
        this.dispatch(
            {
                type: this.LOGIN_START,
                username,
                password
            }
        );
    }

    loginSSO() {
        this.dispatch(
            {
                type: this.LOGIN_SSO_START
            }
        );
    }

    logout() {
        this.dispatch(
            {
                type: this.LOGOUT_START
            }
        );
    }

    /**
     * Checks to see if the current user has permissions to edit the given process ID.
     * @param state
     * @param processId
     */
    canCurrentUserEditProcess(processId) {
        const myState = this.getMyState(this.getStore().getState());
        return myState.userLoggedIn && myState.writableProcesses && (processId in myState.writableProcesses);
    }

    /**
     * Fetch if the users have not already ben loaded into the store
     */
    fetchUsersIfNeeded() {
        const myState = this.getMyState(this.getStore().getState());
        if (myState.users === null) {
            this.dispatch({
                type: this.USERS_FETCH_START
            });
        }
    }

    addWritableProcess(newName) {
        this.dispatch({
            type: this.UPDATE_WRITABLE_PROCESSES,
            processId: newName
        });
    }
}

export default new SecurityService();



// WEBPACK FOOTER //
// ./src/services/SecurityService.js